import { AttackPattern, PatternState } from '../AttackPattern.js';
import { Courtier } from '../../../entities/Courtier.js';
import { ARENA } from '../../../../config/constants.js';

const EDGE_MARGIN = 20; // matches MinionWaveSystem's spawn inset
const DEVOUR_GRACE = 2; // px beyond hull contact that still counts as "reached the maw"

/**
 * The Feast (The Famished Court's signature) — the only fight in the game
 * with a second win condition running against you. Courtiers file in from
 * the arena edges and march straight for the Court's open maw; every one
 * that arrives is DEVOURED — the boss heals and gains a `gorged` stack that
 * fattens its Regurgitant Volley for the rest of the fight. The player's
 * weapons auto-aim at the nearest target, so the real mechanic is
 * positioning: stand where your fire walls off the procession, or accept
 * the heal and keep pure boss damage. No other boss makes you triage
 * targets — every other fight is dodging; this one is also arithmetic.
 *
 * The pattern owns its courtiers completely: it spawns them into CombatSystem
 * (so weapons can hit them), drives their marching itself (nothing else
 * updates minions during a boss stage), and sweeps any survivors when the
 * feast ends — no orphaned entities, mirroring EmberWake's "no leftover
 * hazards" rule.
 */
export class TheFeast extends AttackPattern {
  constructor({ minPhase = 0, config, combat }) {
    super({ name: 'The Feast', telegraphDuration: 1.1, activeDuration: 8.0, minPhase });
    this.config = config;
    this.combat = combat ?? null;
  }

  onBegin(boss) {
    this._courtiers = [];
    // Spawn points rolled at telegraph time so the warning marks are honest.
    const count = Math.min(8, this.config.count + boss.phase * 2);
    this._spawnPoints = Array.from({ length: count }, () => this._randomEdgePosition());
  }

  _randomEdgePosition() {
    const side = Math.floor(Math.random() * 4);
    const spanX = ARENA.width - EDGE_MARGIN * 2;
    const spanY = ARENA.height - EDGE_MARGIN * 2;
    switch (side) {
      case 0: return { x: ARENA.x + EDGE_MARGIN + Math.random() * spanX, y: ARENA.y + EDGE_MARGIN };
      case 1: return { x: ARENA.x + ARENA.width - EDGE_MARGIN, y: ARENA.y + EDGE_MARGIN + Math.random() * spanY };
      case 2: return { x: ARENA.x + EDGE_MARGIN + Math.random() * spanX, y: ARENA.y + ARENA.height - EDGE_MARGIN };
      default: return { x: ARENA.x + EDGE_MARGIN, y: ARENA.y + EDGE_MARGIN + Math.random() * spanY };
    }
  }

  onActiveStart(boss, _player, _pool) {
    if (!this.combat) return;
    for (const { x, y } of this._spawnPoints) {
      const courtier = new Courtier(boss.eventBus, {
        x,
        y,
        hp: this.config.courtierHp,
        moveSpeed: this.config.courtierSpeed + boss.phase * 4,
        contactDamage: this.config.contactDamage,
      });
      this._courtiers.push(courtier);
      this.combat.addMinion(courtier);
    }
  }

  onActiveTick(dt, boss, player, _pool) {
    for (let i = this._courtiers.length - 1; i >= 0; i--) {
      const courtier = this._courtiers[i];
      if (courtier.isDead) {
        // Killed by the player — executed before reaching the table.
        this.combat?.removeMinion(courtier);
        this._courtiers.splice(i, 1);
        continue;
      }
      courtier.update(dt, player, boss);
      const dist = Math.hypot(courtier.x - boss.x, courtier.y - boss.y);
      if (dist < boss.radius + courtier.radius + DEVOUR_GRACE) {
        this._devour(boss, courtier);
        this._courtiers.splice(i, 1);
      }
    }
  }

  _devour(boss, courtier) {
    this.combat?.removeMinion(courtier);
    const hpBefore = boss.hp;
    boss.hp = Math.min(boss.maxHp, boss.hp + this.config.healPerCourtier);
    // Gorged stacks persist for the whole fight (the Boss instance lives
    // exactly one fight) — read by RegurgitantVolley.
    boss._gorged = (boss._gorged ?? 0) + 1;
    boss.eventBus?.emit('boss:devoured', { x: courtier.x, y: courtier.y, bossX: boss.x, bossY: boss.y, amount: boss.hp - hpBefore });
  }

  onEnd(boss) {
    // The feast is called off — any courtier still marching scurries back
    // into the dark rather than freezing in place as an orphan.
    if (this._courtiers.length > 0) {
      boss.eventBus?.emit('feast:ended', { fled: this._courtiers.map((c) => ({ x: c.x, y: c.y })) });
    }
    for (const courtier of this._courtiers) this.combat?.removeMinion(courtier);
    this._courtiers = [];
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return {
        kind: 'feastMarks',
        x: boss.x,
        y: boss.y,
        t: 1 - this._timer / this.telegraphDuration,
        marks: this._spawnPoints,
      };
    }
    if (this.state === PatternState.ACTIVE && this._courtiers.length > 0) {
      // Procession lines from each living courtier to the maw — the routes
      // the player is being asked to cut.
      return {
        kind: 'feastMarks',
        x: boss.x,
        y: boss.y,
        t: 1,
        marks: this._courtiers.map((c) => ({ x: c.x, y: c.y })),
        marching: true,
      };
    }
    return null;
  }
}
